import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Axios from "../services/AxiosInterceptor";
import { Eye, EyeOff, Mail, Lock, ArrowLeft, RefreshCcw } from 'lucide-react';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ResetPassword() {
  const query = useQuery();
  const navigate = useNavigate();

  const token = query.get("token") || "";
  const emailFromQuery = query.get("email") || "";

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);

  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [success, setSuccess] = useState(false);

  const showNotice = (msg, isSuccess = false) => {
    setPopupMessage(msg);
    setShowPopup(true);
    setSuccess(isSuccess);
    setTimeout(() => setShowPopup(false), 3500);
  };

  useEffect(() => {
    if (!token || !emailFromQuery) {
      showNotice("Invalid or expired reset link. Please request a new one.", false);
    }
    setEmail(emailFromQuery);
  }, [token, emailFromQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) return showNotice("Email is required.", false);
    if (!token) return showNotice("Reset token missing.", false);
    if (!password || password.length < 6) return showNotice("Password must be at least 6 characters.", false);
    if (password !== confirm) return showNotice("Passwords do not match.", false);

    try {
      setLoading(true);
      const payload = { email, token, newPassword: password };
      const res = await Axios.post("api/reset_password", payload);
      const data = res?.data;

      if (data && data.success) {
        showNotice("Password reset successful! Redirecting to login...", true);
        setTimeout(() => navigate("/cx_Login"), 2000);
      } else {
        showNotice(data?.message || "Reset failed.", false);
      }
    } catch (err) {
      const serverMsg = err.response?.data?.message;
      showNotice(serverMsg || "Something went wrong.", false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showPopup && (
        <div className={`fixed bottom-6 right-6 max-w-sm px-6 py-4 text-white rounded-xl shadow-2xl text-sm font-medium z-[999999999] animate-slide-up ${
          success ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-pink-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {popupMessage}
          </div>
        </div>
      )}

      <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-4">
        
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Left Side - Branding */}
          <div className="hidden lg:flex relative bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-8 flex-col justify-center overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <RefreshCcw size={40} className="text-white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">Reset Your Password</h2>
              <p className="text-orange-100 text-lg mb-8">Create a strong new password for your account</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Choose a strong password</p>
                    <p className="text-xs text-orange-100">At least 6 characters long</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Confirm your password</p>
                    <p className="text-xs text-orange-100">Make sure they match</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-white bg-white/10 rounded-xl p-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold">3</span>
                  </div>
                  <div className="text-left text-sm">
                    <p className="font-medium">Save and login</p>
                    <p className="text-xs text-orange-100">You're all set!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 bg-white">
            
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Reset Password</h1>
              <p className="text-gray-500">
                Enter a new password for <strong>{email || "your account"}</strong>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Email (readonly) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-orange-500" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-orange-500" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-12 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 text-gray-800"
                    placeholder="Enter new password (min 6 characters)"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-orange-500" />
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-4 py-2.5 pr-12 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 text-gray-800"
                    placeholder="Re-enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <RefreshCcw size={20} />
                    Save New Password
                  </>
                )}
              </button>
            </form>

            <div className="mt-6">
              <button
                onClick={() => navigate("/cx_Login")}
                className="w-full py-2.5 border-2 border-gray-200 hover:border-orange-300 text-gray-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 hover:bg-orange-50"
              >
                <ArrowLeft size={20} />
                Back to Login
              </button>
            </div>

            <div className="lg:hidden mt-6 text-center">
              <p className="text-xs text-gray-400">© 2024 Yogamatic. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
}
